import { useEffect, useState } from "react";
import { ShieldCheck, Loader2, Search, FileDown, Trash2, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { downloadGdprExport } from "../../components/GdprClientePanel";

export const fmtIt = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleString("it-IT", { timeZone: "Europe/Rome" }); } catch { return s; } };
const AZ = { consultazione: "text-sky-400", modifica: "text-amber-400", export: "text-violet-400", anonimizzazione: "text-red-400", cancellazione: "text-red-500", revisione: "text-emerald-400" };

const InformativaTab = () => {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/gdpr/settings").then(({ data }) => setC(data)).catch(() => {}); }, []);
  if (!c) return <Loader2 className="h-5 w-5 animate-spin text-amber-500" />;
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/gdpr/settings", c); const nv = data.versione !== c.versione; setC(data); toast.success(nv ? `Salvato: nuova versione ${data.versione}, tutti gli utenti dovranno riaccettarla` : "Salvato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <div className="flex items-center justify-between mb-1.5"><span className="text-sm font-semibold text-slate-200">Testo informativa privacy</span><span className="text-xs font-mono text-slate-400" data-testid="gdpr-versione">versione attuale {c.versione}{c.updated_at ? ` · aggiornata ${fmtIt(c.updated_at)}` : ""}</span></div>
        <Textarea rows={16} value={c.testo} onChange={(e) => setC({ ...c, testo: e.target.value })} data-testid="gdpr-testo" className="bg-slate-900/60 border-slate-700 font-mono text-xs" />
        <p className="text-xs text-slate-500 mt-1">Modificando il testo si crea una nuova versione: tutti gli utenti (studio, professionisti, segnalatori e clienti) dovranno accettarla al prossimo accesso.</p>
      </div>
      <div>
        <span className="text-sm font-semibold text-slate-200">Testo banner cookie (portale professionisti e area clienti)</span>
        <Textarea rows={3} value={c.cookie_testo} onChange={(e) => setC({ ...c, cookie_testo: e.target.value })} data-testid="gdpr-cookie-testo" className="mt-1.5 bg-slate-900/60 border-slate-700 text-xs" />
      </div>
      <Button onClick={save} disabled={busy} data-testid="gdpr-save" className="bg-amber-500 text-[#0a0d14] hover:bg-amber-400">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Salva</Button>
    </div>
  );
};

export const LogTable = ({ rows, showStudio }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-800">
    <table className="w-full text-xs" data-testid="gdpr-log-table">
      <thead className="border-b border-slate-700 text-slate-500 font-semibold uppercase tracking-wide"><tr>
        <th className="text-left px-3 py-2">Data e ora</th>{showStudio && <th className="text-left px-3 py-2">Studio</th>}<th className="text-left px-3 py-2">Cliente</th><th className="text-left px-3 py-2">Azione</th><th className="text-left px-3 py-2">Utente</th><th className="text-left px-3 py-2">Dettaglio</th><th className="text-left px-3 py-2">IP</th>
      </tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">Nessun accesso registrato</td></tr>}
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-slate-800 hover:bg-slate-800/30" data-testid={`gdpr-log-row-${r.id}`}>
            <td className="px-3 py-1.5 font-mono whitespace-nowrap text-slate-300">{fmtIt(r.at)}</td>{showStudio && <td className="px-3 py-1.5 text-slate-300">{r.studio}</td>}
            <td className="px-3 py-1.5 text-slate-100">{r.cliente_nome}</td><td className={`px-3 py-1.5 font-semibold ${AZ[r.azione] || ""}`}>{r.azione}</td>
            <td className="px-3 py-1.5 text-slate-300">{r.user_nome}<span className="text-slate-500"> · {r.ruolo}</span></td><td className="px-3 py-1.5 text-slate-400">{r.dettaglio}</td><td className="px-3 py-1.5 font-mono text-slate-500">{r.ip}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const LogTab = ({ url, showStudio, tenants }) => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [tid, setTid] = useState("");
  const load = () => { setRows(null); api.get(url, { params: { q, tenant_id: tid } }).then(({ data }) => setRows(data)).catch(() => setRows([])); };
  useEffect(() => { load(); }, [tid]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3">
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-wrap gap-2">
        {tenants && (
          <select value={tid} onChange={(e) => setTid(e.target.value)} data-testid="gdpr-log-studio" className="h-9 rounded-md border border-slate-700 bg-slate-900/60 px-2 text-sm text-slate-200">
            <option value="">Tutti gli studi</option>{tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente, utente o dettaglio" data-testid="gdpr-log-search" className="h-9 w-72 bg-slate-900/60 border-slate-700" />
        <Button type="submit" variant="outline" data-testid="gdpr-log-cerca"><Search className="h-4 w-4 mr-1" /> Cerca</Button>
      </form>
      {rows === null ? <Loader2 className="h-5 w-5 animate-spin text-amber-500" /> : <LogTable rows={rows} showStudio={showStudio} />}
    </div>
  );
};

const OblioRow = ({ c, onDone }) => {
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState("");
  const run = async (modo) => {
    if (motivo.trim().length < 3) { toast.error("Indica il motivo della richiesta"); return; }
    const msg = modo === "cancella" ? `CANCELLARE definitivamente ${c.nome}? L'anagrafica e l'account area clienti vengono eliminati e i suoi dati nelle pratiche resi anonimi. Operazione irreversibile.`
      : `ANONIMIZZARE ${c.nome}? Tutti i dati personali verranno sostituiti con "CLIENTE ANONIMIZZATO". Operazione irreversibile.`;
    if (!window.confirm(msg)) return;
    setBusy(modo);
    try { const { data } = await api.post(`/admin/gdpr/clienti/${c.id}/oblio`, { modo, motivo }); toast.success(`Fatto: ${data.pratiche_aggiornate} pratiche aggiornate`); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const exp = async (f) => { try { await downloadGdprExport(c.id, f, c.tenant_id); } catch { toast.error("Esportazione non riuscita"); } };
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 space-y-2" data-testid={`gdpr-cliente-${c.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold text-slate-100">{c.nome}</span><span className="text-slate-500">{c.codice_fiscale}</span><span className="text-xs text-slate-400">· {c.studio}</span>
        {c.anonimizzato && <span className="text-[11px] px-2 py-0.5 rounded-full border border-red-500/50 text-red-400">anonimizzato</span>}
        <span className="ml-auto flex gap-1">{["pdf", "csv"].map((f) => <Button key={f} size="sm" variant="outline" onClick={() => exp(f)} data-testid={`gdpr-export-${f}-${c.id}`}><FileDown className="h-3.5 w-3.5 mr-1" />{f.toUpperCase()}</Button>)}</span>
      </div>
      {!c.anonimizzato && (
        <div className="flex flex-wrap gap-2">
          <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo / riferimento richiesta dell'interessato" data-testid={`gdpr-oblio-motivo-${c.id}`} className="h-8 flex-1 min-w-[220px] bg-slate-900/60 border-slate-700 text-xs" />
          <Button size="sm" disabled={!!busy} onClick={() => run("anonimizza")} data-testid={`gdpr-oblio-anonimizza-${c.id}`} className="bg-amber-600 hover:bg-amber-500 text-white">{busy === "anonimizza" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <EyeOff className="h-3.5 w-3.5 mr-1" />} Anonimizza</Button>
          <Button size="sm" disabled={!!busy} onClick={() => run("cancella")} data-testid={`gdpr-oblio-cancella-${c.id}`} className="bg-red-600 hover:bg-red-500 text-white">{busy === "cancella" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />} Cancella</Button>
        </div>
      )}
    </div>
  );
};

const OblioTab = ({ tenants }) => {
  const [q, setQ] = useState("");
  const [tid, setTid] = useState("");
  const [res, setRes] = useState(null);
  const [hist, setHist] = useState([]);
  const loadHist = () => api.get("/admin/gdpr/oblio-log").then(({ data }) => setHist(data)).catch(() => {});
  useEffect(() => { loadHist(); }, []);
  const search = async (e) => { e?.preventDefault(); try { const { data } = await api.get("/admin/gdpr/clienti", { params: { q, tenant_id: tid } }); setRes(data); } catch { setRes([]); } };
  return (
    <div className="space-y-4">
      <form onSubmit={search} className="flex flex-wrap gap-2">
        <select value={tid} onChange={(e) => setTid(e.target.value)} data-testid="gdpr-oblio-studio" className="h-9 rounded-md border border-slate-700 bg-slate-900/60 px-2 text-sm text-slate-200">
          <option value="">Tutti gli studi</option>{tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, cognome, codice fiscale o email (min 2 caratteri)" data-testid="gdpr-oblio-search" className="h-9 w-80 bg-slate-900/60 border-slate-700" />
        <Button type="submit" variant="outline" data-testid="gdpr-oblio-cerca"><Search className="h-4 w-4 mr-1" /> Cerca cliente</Button>
      </form>
      {res && (res.length ? <div className="space-y-2">{res.map((c) => <OblioRow key={c.id} c={c} onDone={() => { search(); loadHist(); }} />)}</div> : <p className="text-sm text-slate-500">Nessun cliente trovato</p>)}
      <div>
        <h3 className="text-sm font-semibold text-slate-200 mb-2">Registro operazioni di oblio</h3>
        <div className="overflow-x-auto rounded-lg border border-slate-800">
          <table className="w-full text-xs" data-testid="gdpr-oblio-log">
            <thead className="border-b border-slate-700 text-slate-500 font-semibold uppercase"><tr><th className="text-left px-3 py-2">Data</th><th className="text-left px-3 py-2">Studio</th><th className="text-left px-3 py-2">Cliente</th><th className="text-left px-3 py-2">Operazione</th><th className="text-left px-3 py-2">Motivo</th><th className="text-left px-3 py-2">Eseguito da</th></tr></thead>
            <tbody>
              {hist.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-500">Nessuna operazione</td></tr>}
              {hist.map((h) => <tr key={h.id} className="border-t border-slate-800"><td className="px-3 py-1.5 font-mono">{fmtIt(h.at)}</td><td className="px-3 py-1.5">{h.studio}</td><td className="px-3 py-1.5">{h.cliente_nome}</td><td className="px-3 py-1.5 font-semibold text-red-400">{h.modo === "cancella" ? "Cancellazione" : "Anonimizzazione"} · {h.pratiche_aggiornate} pratiche</td><td className="px-3 py-1.5 text-slate-400">{h.motivo}</td><td className="px-3 py-1.5">{h.eseguito_da}</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const RetentionTab = () => {
  const [cfg, setCfg] = useState(null);
  const [anni, setAnni] = useState("");
  const [list, setList] = useState(null);
  const load = () => { setList(null); api.get("/admin/gdpr/retention").then(({ data }) => setList(data)).catch(() => setList({ clienti: [] })); };
  useEffect(() => { api.get("/admin/gdpr/settings").then(({ data }) => { setCfg(data); setAnni(String(data.retention_anni)); }).catch(() => {}); load(); }, []);
  const saveAnni = async () => {
    try { const { data } = await api.put("/admin/gdpr/settings", { retention_anni: Number(anni) }); setCfg(data); toast.success("Periodo di conservazione salvato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const ok = async (c) => {
    const nota = window.prompt(`Confermi che i dati di ${c.nome} vanno mantenuti? Nota (facoltativa):`, "");
    if (nota === null) return;
    try { await api.post(`/admin/gdpr/retention/${c.id}/revisionato`, { nota }); toast.success("Revisione registrata"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <div><span className="text-sm text-slate-300">Segnala per revisione i clienti inattivi da (anni)</span>
          <Input type="number" min={1} max={50} value={anni} onChange={(e) => setAnni(e.target.value)} data-testid="gdpr-retention-anni" className="mt-1 h-9 w-28 bg-slate-900/60 border-slate-700" /></div>
        <Button onClick={saveAnni} disabled={!cfg} data-testid="gdpr-retention-save" className="bg-amber-500 text-[#0a0d14] hover:bg-amber-400">Salva</Button>
      </div>
      <p className="text-xs text-slate-500">Nessun dato viene cancellato automaticamente: i clienti senza attività (anagrafica e pratiche) oltre il periodo vengono solo segnalati. Puoi confermarne la conservazione (non verranno risegnalati per 12 mesi) o procedere dal tab "Diritto all'oblio".</p>
      {list === null ? <Loader2 className="h-5 w-5 animate-spin text-amber-500" /> : (
        <div className="overflow-x-auto rounded-lg border border-slate-800">
          <table className="w-full text-xs" data-testid="gdpr-retention-table">
            <thead className="border-b border-slate-700 text-slate-500 font-semibold uppercase"><tr><th className="text-left px-3 py-2">Cliente</th><th className="text-left px-3 py-2">Studio</th><th className="text-left px-3 py-2">Ultima attività</th><th className="px-3 py-2" /></tr></thead>
            <tbody>
              {list.clienti.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-500">Nessun cliente da revisionare</td></tr>}
              {list.clienti.map((c) => (
                <tr key={c.id} className="border-t border-slate-800" data-testid={`gdpr-retention-row-${c.id}`}>
                  <td className="px-3 py-1.5 text-slate-100">{c.nome} <span className="text-slate-500">{c.codice_fiscale}</span></td><td className="px-3 py-1.5">{c.studio}</td><td className="px-3 py-1.5 font-mono">{fmtIt(c.ultima_attivita)}</td>
                  <td className="px-3 py-1.5 text-right"><Button size="sm" variant="outline" onClick={() => ok(c)} data-testid={`gdpr-retention-ok-${c.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mantieni</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function Gdpr() {
  const [tenants, setTenants] = useState([]);
  useEffect(() => { api.get("/admin/tenants").then(({ data }) => setTenants((data || []).map((t) => ({ id: t.id, name: t.name || t.nome || t.studio_name || t.id })))).catch(() => {}); }, []);
  return (
    <div data-testid="admin-gdpr-page">
      <PageHeader title="Privacy e GDPR" subtitle="Regolamento UE 2016/679: informativa, log accessi, diritto all'oblio e conservazione dei dati." icon={ShieldCheck} testId="gdpr-header" />
      <Tabs defaultValue="informativa">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="informativa" data-testid="gdpr-tab-informativa">Informativa e cookie</TabsTrigger>
          <TabsTrigger value="log" data-testid="gdpr-tab-log">Log accessi</TabsTrigger>
          <TabsTrigger value="oblio" data-testid="gdpr-tab-oblio">Diritto all'oblio ed export</TabsTrigger>
          <TabsTrigger value="retention" data-testid="gdpr-tab-retention">Conservazione dati</TabsTrigger>
        </TabsList>
        <TabsContent value="informativa"><InformativaTab /></TabsContent>
        <TabsContent value="log"><LogTab url="/admin/gdpr/log" showStudio tenants={tenants} /></TabsContent>
        <TabsContent value="oblio"><OblioTab tenants={tenants} /></TabsContent>
        <TabsContent value="retention"><RetentionTab /></TabsContent>
      </Tabs>
    </div>
  );
}
